import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
    FormProvider,
    useFieldArray,
    useForm,
    useFormContext,
    useWatch,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod'
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react'

import type {
    CreateInNoteItem,
    CreateInNoteRequest,
} from '@/lib/api/product-lot-api'
import type { ProductLotOption } from '@/components/product-lots/ProductLotItemsField'
import type { LotOption } from '@/components/lots/lot-items-field'
import { productLotToOption } from '@/components/product-lots/product-lot-option'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { ProductLotItemsField } from '@/components/product-lots/ProductLotItemsField'
import { LotSourcesSubTable } from '@/components/lots/lot-sources-subtable'
import {
    ScanLotCodeButton,
    makeLotIdParser,
} from '@/components/lots/scan-lot-code-button'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useCreateProductLotInNote } from '@/hooks/use-product-lot-mutation'
import { useProductLotsQuery } from '@/hooks/use-product-lot-query'
import { useProducts } from '@/hooks/use-product-query'
import { renderProductOption } from '@/components/products/product-picker'
import {
    useScentingLots,
    useScentingWithdrawalNote,
    useScentingWithdrawalNotesInfinite,
} from '@/hooks/use-scenting-lot-query'
import {
    packagingLotOption,
    scentingLotOption,
} from '@/components/lots/lot-option-builders'
import { formatQty } from '@/lib/format'
import {
    usePackagingLots,
    usePackagingWithdrawalNote,
    usePackagingWithdrawalNotesInfinite,
} from '@/hooks/use-packaging-lot-query'
import { useDebounced } from '@/hooks/use-debounced'
import { productLotApi } from '@/lib/api/product-lot-api'
import { normalizeLotDiscount } from '@/lib/lot-discount'
import { m } from '@/lib/paraglide/messages'

// Nguồn tầng dòng (reason=from_packaging): lô lấy hương nguồn + kg. Bắt buộc kiểm
// ở superRefine cấp form (biết được reason). Ở đây chỉ khai optional.
const sourceItemFields = {
    // Danh sách nhiều lô lấy hương nguồn (tuỳ chọn ở tầng field). Bắt buộc ≥1 khi
    // reason=from_packaging kiểm ở superRefine cấp form.
    sources: z
        .array(
            z.object({
                source_lot_id: z.string().optional(),
                source_quantity: z.number().optional(),
            }),
        )
        .optional(),
    // Bảng vật tư con (tuỳ chọn). Giữ permissive ở tầng field — bắt buộc từng dòng
    // (đã thêm thì phải chọn lô + kg > 0) kiểm ở superRefine cấp form.
    materials: z
        .array(
            z.object({
                packaging_lot_id: z.string().optional(),
                quantity: z.number().optional(),
            }),
        )
        .optional(),
}

const itemSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('new'),
        new_lot_code: z.string().optional(),
        new_lot_product_id: z
            .string({ message: m.form_err_pick_product() })
            .min(1, m.form_err_pick_product()),
        new_lot_warehouse_id: z
            .string()
            .min(1, m.form_err_pick_warehouse())
            .optional(),
        new_lot_position_id: z
            .string({ message: m.warehouse_position_required() })
            .uuid({ message: m.warehouse_position_required() }),
        new_lot_manufactured_date: z.string().optional(),
        new_lot_expiry_date: z.string().optional(),
        // Tuỳ chọn — bỏ trống nghĩa là lô không đánh dấu CK. Input dùng
        // setValueAs (xem ProductLotItemsField) nên ô rỗng vào đây là
        // undefined, không phải NaN.
        new_lot_discount_percent: z
            .number()
            .min(0, m.form_err_discount_range())
            .max(100, m.form_err_discount_range())
            .optional(),
        quantity: z
            .number({ message: m.form_err_quantity_required() })
            .positive(m.form_err_quantity_positive()),
        ...sourceItemFields,
    }),
    z.object({
        kind: z.literal('existing'),
        product_lot_id: z
            .string({ message: m.form_err_pick_existing_lot() })
            .min(1, m.form_err_pick_existing_lot()),
        quantity: z
            .number({ message: m.form_err_quantity_required() })
            .positive(m.form_err_quantity_positive()),
        ...sourceItemFields,
    }),
])

// Enum đủ 4 lý do PNK thành phẩm — khi sửa 1 phiếu tạo tự động (from_order_cancel,
// from_customer_return) resolver vẫn phải chấp nhận reason đó. Màn tạo chỉ cho
// chọn 2 lý do đầu (xem Select bên dưới); màn sửa khoá reason.
const schema = z
    .object({
        reason: z.enum([
            'from_packaging',
            'adjustment',
            'from_manufacturing_partner',
            'from_customer_return',
            'from_order_cancel',
        ]),
        // Bắt buộc khi reason=from_packaging (xem superRefine). PXK vật tư luôn
        // tuỳ chọn — không phải SP nào cũng tốn vật tư đóng gói.
        scenting_lot_stock_out_note_id: z.string().optional(),
        packaging_lot_stock_out_note_id: z.string().optional(),
        notes: z.string().max(2000, m.form_err_notes_too_long()).optional(),
        items: z.array(itemSchema).min(1, m.form_err_items_min_one()),
    })
    .superRefine((data, ctx) => {
        // Luồng đi chuỗi: mỗi dòng phải khai ≥1 lô lấy hương nguồn; mỗi nguồn có lô
        // + kg xuất >= 0 (cho phép 0, cấm âm/để trống); không khai trùng lô.
        if (data.reason !== 'from_packaging') return
        data.items.forEach((it, idx) => {
            const sources = it.sources ?? []
            if (sources.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['items', idx, 'sources'],
                    message: m.pll_new_err_source_lot(),
                })
            } else {
                const seen = new Set<string>()
                sources.forEach((s, sIdx) => {
                    if (!s.source_lot_id) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['items', idx, 'sources', sIdx, 'source_lot_id'],
                            message: m.pll_new_err_source_lot(),
                        })
                    } else if (seen.has(s.source_lot_id)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['items', idx, 'sources', sIdx, 'source_lot_id'],
                            message: m.pll_new_err_source_dup(),
                        })
                    } else {
                        seen.add(s.source_lot_id)
                    }
                    if (s.source_quantity == null || s.source_quantity < 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [
                                'items',
                                idx,
                                'sources',
                                sIdx,
                                'source_quantity',
                            ],
                            message: m.pll_new_err_source_qty(),
                        })
                    }
                })
            }
            // Vật tư tuỳ chọn — nhưng đã thêm dòng thì phải chọn lô + kg > 0.
            ;(it.materials ?? []).forEach((mat, mIdx) => {
                if (!mat.packaging_lot_id) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['items', idx, 'materials', mIdx, 'packaging_lot_id'],
                        message: m.pll_new_err_material_lot(),
                    })
                }
                if (!mat.quantity || mat.quantity <= 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['items', idx, 'materials', mIdx, 'quantity'],
                        message: m.pll_new_err_material_qty(),
                    })
                }
            })
        })
    })

type FormData = z.infer<typeof schema>

// Picker lô lấy hương nguồn + ô kg xuất + nút Lấy hết cho MỘT dòng (from_packaging).
// TẠM ẨN phiếu lấy hàng khỏi UI (tính năng item-level-lot-lineage chưa hoàn thiện).
// Đặt = true để bật lại 2 picker phiếu lấy hàng (lấy hương + vật tư). Không xoá code/BE.
const WITHDRAWAL_UI_ENABLED = false

// Một dòng vật tư trong bảng con: picker lô vật tư + kg + nút "lấy hết" + xoá.
function MaterialRow({
    idx,
    mIdx,
    options,
    onSearchChange,
    clientFilter,
    onRemove,
}: {
    idx: number
    mIdx: number
    options: LotOption[]
    onSearchChange: (s: string) => void
    clientFilter?: boolean
    onRemove: () => void
}) {
    const { control, register, setValue, formState } =
        useFormContext<FormData>()
    const picked = useWatch({
        control,
        name: `items.${idx}.materials.${mIdx}.packaging_lot_id`,
    })
    // Lô đã chọn ở các dòng vật tư KHÁC của cùng item → loại khỏi options để
    // không chọn trùng (bảng con có UNIQUE(item, lô); trùng sẽ vỡ khi lưu).
    const siblings = useWatch({
        control,
        name: `items.${idx}.materials`,
    }) as Array<{ packaging_lot_id?: string }> | undefined
    const rowOptions = useMemo(
        () =>
            options.filter((o) => {
                if (o.value === picked) return true
                return !(siblings ?? []).some(
                    (s, i) => i !== mIdx && s?.packaging_lot_id === o.value,
                )
            }),
        [options, siblings, mIdx, picked],
    )
    const matErr = (
        formState.errors.items?.[idx] as
            | { materials?: Array<Record<string, { message?: string }>> }
            | undefined
    )?.materials?.[mIdx]
    // Lô vật tư đã chọn được cache để nhãn + hint "còn X" không rớt khỏi trigger
    // khi search server đổi options (server chỉ trả danh sách theo từ khoá).
    const [pickedCache, setPickedCache] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const pickedOption =
        options.find((o) => o.value === picked) ??
        (picked ? pickedCache.get(picked) : undefined)
    const remaining = pickedOption?.available

    return (
        <div className="mt-1 grid gap-2 sm:grid-cols-2">
            <div className="min-w-0">
                <Combobox
                    options={rowOptions}
                    value={picked ?? ''}
                    onChange={(v) => {
                        const opt = options.find((o) => o.value === v)
                        if (opt)
                            setPickedCache((prev) => {
                                if (prev.has(v)) return prev
                                const next = new Map(prev)
                                next.set(v, opt)
                                return next
                            })
                        setValue(
                            `items.${idx}.materials.${mIdx}.packaging_lot_id`,
                            v,
                            { shouldValidate: true },
                        )
                    }}
                    onSearchChange={clientFilter ? undefined : onSearchChange}
                    shouldFilter={!!clientFilter}
                    selectedOption={pickedOption}
                    onOpenChange={
                        clientFilter
                            ? undefined
                            : (open) => {
                                  if (!open) onSearchChange('')
                              }
                    }
                    placeholder={m.pll_new_material_lot_placeholder()}
                />
                {matErr?.packaging_lot_id?.message && (
                    <p className="mt-1 text-xs text-destructive">
                        {matErr.packaging_lot_id.message}
                    </p>
                )}
                {remaining != null && (
                    <p className="mt-1 text-xs text-muted-foreground">
                        {m.lot_items_available_hint({
                            qty: formatQty(remaining),
                            unit: 'kg',
                        })}
                    </p>
                )}
            </div>
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <Input
                            type="number"
                            step="0.001"
                            min="0"
                            placeholder={m.pll_new_material_qty_label()}
                            {...register(
                                `items.${idx}.materials.${mIdx}.quantity`,
                                {
                                    setValueAs: (v) =>
                                        v === '' || v == null
                                            ? undefined
                                            : Number(v),
                                },
                            )}
                        />
                        {remaining != null && remaining > 0 && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="shrink-0"
                                onClick={() =>
                                    setValue(
                                        `items.${idx}.materials.${mIdx}.quantity`,
                                        remaining,
                                        { shouldValidate: true },
                                    )
                                }
                            >
                                {m.lot_items_take_all()}
                            </Button>
                        )}
                    </div>
                    {matErr?.quantity?.message && (
                        <p className="mt-1 text-xs text-destructive">
                            {matErr.quantity.message}
                        </p>
                    )}
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    aria-label={m.pll_new_material_remove()}
                    onClick={onRemove}
                >
                    <Trash2 className="size-4 text-destructive" />
                </Button>
            </div>
        </div>
    )
}

// Bảng vật tư con của MỘT dòng lô sản phẩm (from_packaging, tuỳ chọn).
function MaterialsSubTable({
    idx,
    options,
    onSearchChange,
    clientFilter,
}: {
    idx: number
    options: LotOption[]
    onSearchChange: (s: string) => void
    clientFilter?: boolean
}) {
    const { control } = useFormContext<FormData>()
    const { fields, append, remove } = useFieldArray({
        control,
        name: `items.${idx}.materials`,
    })

    return (
        <div className="mt-1 rounded-md border border-dashed bg-muted/20 p-2">
            <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                    {m.pll_new_materials_title()}
                </span>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                        append({
                            packaging_lot_id: '',
                            quantity: undefined,
                        })
                    }
                >
                    <Plus className="size-4 mr-1" />
                    {m.pll_new_material_add()}
                </Button>
            </div>
            {fields.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                    {m.pll_new_materials_empty()}
                </p>
            ) : (
                fields.map((f, mIdx) => (
                    <MaterialRow
                        key={f.id}
                        idx={idx}
                        mIdx={mIdx}
                        options={options}
                        onSearchChange={onSearchChange}
                        clientFilter={clientFilter}
                        onRemove={() => remove(mIdx)}
                    />
                ))
            )}
        </div>
    )
}

type ProductLotStockInFormProps = {
    mode: 'create' | 'edit'
    title: string
    submitLabel: string
    isPending: boolean
    defaultValues: FormData
    onSubmit: (req: CreateInNoteRequest) => Promise<void>
    onCancel: () => void
    /** Lô lấy hương nguồn đã chọn (để sửa hiện đúng dù ngoài top-search). */
    seedSourceOptions?: LotOption[]
    /** Lô vật tư đã chọn (để sửa hiện đúng dù ngoài top-search / note đã cạn). */
    seedMaterialOptions?: LotOption[]
    /** Phiếu lấy hàng lấy hương đã chọn khi tạo — dùng để sửa pre-select lại picker. */
    initialWithdrawalNoteId?: string
    /** Phiếu lấy hàng vật tư đã chọn khi tạo — dùng để sửa pre-select lại picker. */
    initialPackagingWithdrawalNoteId?: string
    // Lô của item hiện có khi sửa. Lô mới do PNK tạo ra đang 'pending' nên bị query
    // (chỉ trả lô active) lọc mất → seed để ô "Chọn lô" hiển thị đúng nhãn.
    seedLotOptions?: ProductLotOption[]
}

const searchSchema = z.object({
    pxk: z.string().uuid().optional(),
    pxbb: z.string().uuid().optional(),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/stock-in-notes/new',
)({
    component: NewProductLotInNotePage,
    validateSearch: searchSchema,
})

function NewProductLotInNotePage() {
    const navigate = useNavigate()
    const search = Route.useSearch()
    const create = useCreateProductLotInNote()

    const initialNSX = useMemo(() => today(), [])
    const initialHSD = useMemo(() => addOneYear(today()), [])

    const defaultValues = useMemo<FormData>(
        () => ({
            reason: 'from_packaging',
            scenting_lot_stock_out_note_id: search.pxk ?? '',
            packaging_lot_stock_out_note_id: search.pxbb ?? '',
            notes: '',
            items: [
                {
                    kind: 'new' as const,
                    new_lot_product_id: '',
                    // Phải là '' (không để undefined): z.string().uuid() nhận
                    // undefined là lỗi *type* fatal → zod v4 abort cả item, bỏ
                    // qua superRefine cấp form → lỗi "lô nguồn/kg" không hiện.
                    // '' fail .uuid() non-fatal nên superRefine vẫn chạy.
                    new_lot_position_id: '',
                    new_lot_manufactured_date: initialNSX,
                    new_lot_expiry_date: initialHSD,
                    quantity: 0,
                    materials: [],
                } as FormData['items'][number],
            ],
        }),
        [search.pxk, search.pxbb, initialNSX, initialHSD],
    )

    const handleSubmit = async (req: CreateInNoteRequest) => {
        const res = await create.mutateAsync(req)
        const noteId = (res as { data?: { id?: string } }).data?.id
        if (noteId) {
            void navigate({
                to: '/sales-warehouse/stock-in-notes/$id',
                params: { id: noteId },
                replace: true,
            })
        }
    }

    return (
        <ProductLotStockInForm
            mode="create"
            title={m.pll_new_pnk_title()}
            submitLabel={m.sl_new_submit_pnk()}
            isPending={create.isPending}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            onCancel={() =>
                void navigate({
                    to: '/sales-warehouse/stock-in-notes',
                    search: { page: 1 },
                })
            }
        />
    )
}

export function ProductLotStockInForm({
    mode,
    title,
    submitLabel,
    isPending,
    defaultValues,
    onSubmit,
    onCancel,
    seedLotOptions = [],
    seedSourceOptions = [],
    seedMaterialOptions = [],
    initialWithdrawalNoteId,
    initialPackagingWithdrawalNoteId,
}: ProductLotStockInFormProps) {
    const isEdit = mode === 'edit'

    const [productSearch, setProductSearch] = useState('')
    const debouncedProductSearch = useDebounced(productSearch, 300)

    const { data: productsData } = useProducts({
        search: debouncedProductSearch || undefined,
        is_active: true,
        per_page: 50,
    })
    const { data: lotsData } = useProductLotsQuery({ per_page: 500 })

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues,
    })

    const reason = useWatch({ control: form.control, name: 'reason' })

    const isFromPackaging = reason === 'from_packaging'

    // Picker lô lấy hương nguồn (per-line): tìm phía server + chỉ lô còn tồn.
    const [sourceSearch, setSourceSearch] = useState('')
    const debouncedSourceSearch = useDebounced(sourceSearch, 300)
    const { data: sourceLotsData } = useScentingLots(
        {
            per_page: 50,
            only_available: '1',
            search: debouncedSourceSearch || undefined,
        },
        { enabled: isFromPackaging },
    )

    // Gợi ý theo phiếu lấy hàng kho lấy hương (tuỳ chọn). Search theo tiêu đề,
    // debounce qua API, cuộn tải thêm. Chọn note → picker lô nguồn chỉ hiện lô
    // trong note. BE trừ số dư note khi tạo.
    const [withdrawalNoteId, setWithdrawalNoteId] = useState(
        initialWithdrawalNoteId ?? '',
    )
    const [withdrawalSearch, setWithdrawalSearch] = useState('')
    const debouncedWithdrawalSearch = useDebounced(withdrawalSearch, 300)
    const withdrawalNotesQuery = useScentingWithdrawalNotesInfinite(
        {
            per_page: 20,
            status: 'open',
            search: debouncedWithdrawalSearch || undefined,
        },
        { enabled: isFromPackaging },
    )
    const withdrawalNoteOptions = useMemo(
        () =>
            (withdrawalNotesQuery.data?.pages ?? [])
                .flatMap((p) => p.data ?? [])
                .map((n) => ({
                    value: n.id,
                    label: `${n.notes ?? n.code} · ${n.code}`,
                })),
        [withdrawalNotesQuery.data],
    )
    const { data: pickedNoteData } = useScentingWithdrawalNote(
        withdrawalNoteId || undefined,
    )
    const noteSourceOptions = useMemo<LotOption[]>(
        () =>
            (pickedNoteData?.data?.items ?? [])
                .filter((it) => it.quantity > 0)
                .map((it) => ({
                    value: it.scenting_lot_id,
                    label: `${it.scenting_lot_code ?? it.scenting_lot_id} — ${it.grade_name ?? ''} (${m.scl_new_source_remaining({ qty: formatQty(it.quantity) })})`,
                    available: it.quantity,
                    unit: 'kg',
                })),
        [pickedNoteData],
    )

    const usingNote = !!withdrawalNoteId
    const sourceLotOptions = useMemo<LotOption[]>(() => {
        const base = usingNote
            ? noteSourceOptions
            : (sourceLotsData?.data ?? []).map(scentingLotOption)
        const map = new Map<string, LotOption>()
        for (const opt of seedSourceOptions) map.set(opt.value, opt)
        for (const opt of base) map.set(opt.value, opt) // base thắng (số dư mới)
        return Array.from(map.values())
    }, [usingNote, noteSourceOptions, sourceLotsData, seedSourceOptions])

    // ---- Vật tư (tuỳ chọn): picker lô vật tư + phiếu lấy hàng kho vật tư ----
    // Tìm lô vật tư phía server (khi KHÔNG chọn phiếu lấy hàng vật tư).
    const [materialSearch, setMaterialSearch] = useState('')
    const debouncedMaterialSearch = useDebounced(materialSearch, 300)
    const { data: materialLotsData } = usePackagingLots(
        {
            per_page: 50,
            only_available: '1',
            search: debouncedMaterialSearch || undefined,
        },
        { enabled: isFromPackaging },
    )

    // Gợi ý theo phiếu lấy hàng kho vật tư (tuỳ chọn, tách hẳn phiếu lấy hương).
    const [packagingWithdrawalNoteId, setPackagingWithdrawalNoteId] = useState(
        initialPackagingWithdrawalNoteId ?? '',
    )
    const [pkgWithdrawalSearch, setPkgWithdrawalSearch] = useState('')
    const debouncedPkgWithdrawalSearch = useDebounced(pkgWithdrawalSearch, 300)
    const pkgWithdrawalNotesQuery = usePackagingWithdrawalNotesInfinite(
        {
            per_page: 20,
            status: 'open',
            search: debouncedPkgWithdrawalSearch || undefined,
        },
        { enabled: isFromPackaging },
    )
    const pkgWithdrawalNoteOptions = useMemo(
        () =>
            (pkgWithdrawalNotesQuery.data?.pages ?? [])
                .flatMap((p) => p.data ?? [])
                .map((n) => ({
                    value: n.id,
                    label: `${n.notes ?? n.code} · ${n.code}`,
                })),
        [pkgWithdrawalNotesQuery.data],
    )
    const { data: pickedPkgNoteData } = usePackagingWithdrawalNote(
        packagingWithdrawalNoteId || undefined,
    )
    const noteMaterialOptions = useMemo<LotOption[]>(
        () =>
            (pickedPkgNoteData?.data?.items ?? [])
                .filter((it) => it.quantity > 0)
                .map((it) => ({
                    value: it.packaging_lot_id,
                    label: `${it.packaging_lot_code ?? it.packaging_lot_id} — ${it.material_name ?? ''} (${m.scl_new_source_remaining({ qty: formatQty(it.quantity) })})`,
                    available: it.quantity,
                    unit: 'kg',
                })),
        [pickedPkgNoteData],
    )

    const usingPkgNote = !!packagingWithdrawalNoteId
    const materialLotOptions = useMemo<LotOption[]>(() => {
        const base = usingPkgNote
            ? noteMaterialOptions
            : (materialLotsData?.data ?? []).map(packagingLotOption)
        const map = new Map<string, LotOption>()
        for (const opt of seedMaterialOptions) map.set(opt.value, opt)
        for (const opt of base) map.set(opt.value, opt)
        return Array.from(map.values())
    }, [usingPkgNote, noteMaterialOptions, materialLotsData, seedMaterialOptions])

    // Đổi lý do sang "điều chỉnh" thì 2 link nguồn không còn nghĩa — xoá khỏi
    // form state. Khoá reason ở edit nên effect này chỉ chạy thực ở màn tạo.
    useEffect(() => {
        if (isFromPackaging) return
        form.setValue('scenting_lot_stock_out_note_id', '')
        form.setValue('packaging_lot_stock_out_note_id', '')
    }, [isFromPackaging, form])

    const productOptions = useMemo(
        () =>
            (productsData?.data ?? []).map((p) => ({
                value: p.id,
                label: p.name,
                unit: p.unit,
                // Dropdown row giống picker màn tạo đơn: ảnh + tên + SKU · KLT ·
                // unit + badge tồn (p.stock_available từ list /products).
                render: () => renderProductOption(p),
            })),
        [productsData],
    )
    const lotOptions = useMemo(() => {
        const fetched = (lotsData?.data ?? []).map(productLotToOption)
        // Seed trước, fetched ghi đè (lô active có đủ ảnh/NSX/HSD/tồn mới hơn);
        // lô pending của phiếu không nằm trong fetched nên giữ nhãn seed.
        return mergeLotOptions(seedLotOptions, fetched)
    }, [lotsData, seedLotOptions])

    const [scannedLots, setScannedLots] = useState<
        Map<
            string,
            { value: string; label: string; available?: number; unit?: string }
        >
    >(new Map())
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await productLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, productLotToOption(l))
                return next
            })
        } catch {
            // Bỏ qua — row vẫn giữ đúng ID trong form state.
        }
    }

    // Lỗi thiếu vật tư đóng gói do BE trả về. Toast biến mất nhanh mà danh sách
    // vật tư thiếu cần đọc kỹ → giữ luôn trên form cho tới lần submit sau.
    const [submitError, setSubmitError] = useState<string | null>(null)

    const submit = form.handleSubmit(async (data) => {
        setSubmitError(null)
        const isChain = data.reason === 'from_packaging'
        const items: CreateInNoteItem[] = data.items.map((it) => {
            const src = isChain
                ? {
                      sources: (it.sources ?? [])
                          .filter((s) => s.source_lot_id)
                          .map((s) => ({
                              source_lot_id: s.source_lot_id as string,
                              source_quantity: Number(s.source_quantity ?? 0),
                          })),
                      // Vật tư tuỳ chọn — chỉ gửi dòng đã chọn lô + kg hợp lệ.
                      materials: (it.materials ?? [])
                          .filter(
                              (mrow) =>
                                  mrow.packaging_lot_id &&
                                  mrow.quantity &&
                                  mrow.quantity > 0,
                          )
                          .map((mrow) => ({
                              packaging_lot_id: mrow.packaging_lot_id as string,
                              quantity: Number(mrow.quantity),
                          })),
                  }
                : {}
            return it.kind === 'new'
                ? ({
                      new_lot_product_id: it.new_lot_product_id,
                      new_lot_position_id: it.new_lot_position_id,
                      new_lot_code: it.new_lot_code || undefined,
                      new_lot_manufactured_date:
                          it.new_lot_manufactured_date || undefined,
                      new_lot_expiry_date: it.new_lot_expiry_date || undefined,
                      new_lot_discount_percent: normalizeLotDiscount(
                          it.new_lot_discount_percent,
                      ),
                      quantity: Number(it.quantity),
                      ...src,
                  } satisfies CreateInNoteItem)
                : ({
                      product_lot_id: it.product_lot_id,
                      quantity: Number(it.quantity),
                      ...src,
                  } satisfies CreateInNoteItem)
        })

        try {
            await onSubmit({
                reason: data.reason,
                notes: data.notes || undefined,
                // Cả PXK chè lẫn PXK vật tư do hệ thống tự sinh lúc duyệt.
                source_withdrawal_note_id:
                    isChain && withdrawalNoteId ? withdrawalNoteId : undefined,
                packaging_withdrawal_note_id:
                    isChain && packagingWithdrawalNoteId
                        ? packagingWithdrawalNoteId
                        : undefined,
                items,
            })
        } catch (err) {
            const msg =
                err instanceof Error ? err.message : m.sl_new_error_create()
            setSubmitError(msg)
            toast.error(msg)
        }
    })

    return (
        <div className="space-y-4 p-4 pb-24 md:p-6 md:pb-6">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={onCancel}>
                    <ArrowLeft className="size-4 mr-1" /> {m.sl_lot_back()}
                </Button>
                <h1 className="text-xl font-semibold">{title}</h1>
            </div>

            <FormProvider {...form}>
                <form
                    onSubmit={submit}
                    className="space-y-3 md:space-y-6 max-w-5xl"
                >
                    <div className="grid gap-4 md:grid-cols-2 rounded-md border bg-card p-4 [&>*]:min-w-0">
                        <div>
                            <Label>{m.sl_new_reason_in()}</Label>
                            <Select
                                value={reason}
                                disabled={isEdit}
                                onValueChange={(v) =>
                                    form.setValue(
                                        'reason',
                                        v as FormData['reason'],
                                    )
                                }
                            >
                                <SelectTrigger className="w-full mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="from_packaging">
                                        {m.pll_in_reason_from_packaging()}
                                    </SelectItem>
                                    <SelectItem value="adjustment">
                                        {m.pll_in_reason_adjustment()}
                                    </SelectItem>
                                    <SelectItem value="from_manufacturing_partner">
                                        {m.pll_in_reason_from_manufacturing_partner()}
                                    </SelectItem>
                                    {/* Chỉ hiện khi phiếu đang sửa mang lý do tạo tự động,
                                        để Select disabled vẫn render đúng nhãn. */}
                                    {reason === 'from_customer_return' && (
                                        <SelectItem value="from_customer_return">
                                            {m.pll_in_reason_from_customer_return()}
                                        </SelectItem>
                                    )}
                                    {reason === 'from_order_cancel' && (
                                        <SelectItem value="from_order_cancel">
                                            {m.pll_in_reason_from_order_cancel()}
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{m.sl_new_notes()}</Label>
                            <textarea
                                {...form.register('notes')}
                                placeholder={m.sl_new_notes_placeholder()}
                                rows={2}
                                className="mt-1 w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm"
                            />
                        </div>

                        {WITHDRAWAL_UI_ENABLED && isFromPackaging && (
                            <>
                                <div className="md:col-span-2">
                                    <Label className="mb-1 block">
                                        {m.pll_new_withdrawal_note_label()}
                                    </Label>
                                    <Combobox
                                        options={[
                                            {
                                                value: '',
                                                label: m.pll_new_withdrawal_note_none(),
                                            },
                                            ...withdrawalNoteOptions,
                                        ]}
                                        value={withdrawalNoteId}
                                        onChange={(v) => {
                                            setWithdrawalNoteId(v)
                                            // Đổi phiếu → xoá lô nguồn đã chọn để
                                            // buộc chọn lại theo phiếu mới.
                                            const its =
                                                form.getValues('items') ?? []
                                            its.forEach((_, i) => {
                                                form.setValue(
                                                    `items.${i}.sources`,
                                                    [],
                                                )
                                            })
                                        }}
                                        onSearchChange={setWithdrawalSearch}
                                        shouldFilter={false}
                                        selectedOption={
                                            withdrawalNoteId &&
                                            pickedNoteData?.data
                                                ? {
                                                      value: pickedNoteData.data
                                                          .id,
                                                      label: `${pickedNoteData.data.notes ?? pickedNoteData.data.code} · ${pickedNoteData.data.code}`,
                                                  }
                                                : undefined
                                        }
                                        onOpenChange={(open) => {
                                            if (!open) setWithdrawalSearch('')
                                        }}
                                        onLoadMore={() =>
                                            void withdrawalNotesQuery.fetchNextPage()
                                        }
                                        hasMore={
                                            withdrawalNotesQuery.hasNextPage
                                        }
                                        placeholder={m.pll_new_withdrawal_note_placeholder()}
                                    />
                                    <p className="mt-1 text-sm text-muted-foreground">
                                        {m.pll_new_source_hint()}
                                    </p>
                                </div>

                                <div className="md:col-span-2">
                                    <Label className="mb-1 block">
                                        {m.pll_new_pkg_withdrawal_note_label()}
                                    </Label>
                                    <Combobox
                                        options={[
                                            {
                                                value: '',
                                                label: m.pll_new_withdrawal_note_none(),
                                            },
                                            ...pkgWithdrawalNoteOptions,
                                        ]}
                                        value={packagingWithdrawalNoteId}
                                        onChange={(v) => {
                                            setPackagingWithdrawalNoteId(v)
                                            // Đổi phiếu → xoá vật tư đã chọn để
                                            // buộc chọn lại theo phiếu mới.
                                            const its =
                                                form.getValues('items') ?? []
                                            its.forEach((_, i) => {
                                                form.setValue(
                                                    `items.${i}.materials`,
                                                    [],
                                                )
                                            })
                                        }}
                                        onSearchChange={setPkgWithdrawalSearch}
                                        shouldFilter={false}
                                        selectedOption={
                                            packagingWithdrawalNoteId &&
                                            pickedPkgNoteData?.data
                                                ? {
                                                      value: pickedPkgNoteData
                                                          .data.id,
                                                      label: `${pickedPkgNoteData.data.notes ?? pickedPkgNoteData.data.code} · ${pickedPkgNoteData.data.code}`,
                                                  }
                                                : undefined
                                        }
                                        onOpenChange={(open) => {
                                            if (!open) setPkgWithdrawalSearch('')
                                        }}
                                        onLoadMore={() =>
                                            void pkgWithdrawalNotesQuery.fetchNextPage()
                                        }
                                        hasMore={
                                            pkgWithdrawalNotesQuery.hasNextPage
                                        }
                                        placeholder={m.pll_new_pkg_withdrawal_note_placeholder()}
                                    />
                                    <p className="mt-1 text-sm text-muted-foreground">
                                        {m.pll_new_material_hint()}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>

                    <ProductLotItemsField
                        mode="in"
                        productOptions={productOptions}
                        lotOptions={lotOptions}
                        onProductSearchChange={setProductSearch}
                        scannedLots={Array.from(scannedLots.values())}
                        renderExtraPerItem={
                            isFromPackaging
                                ? (idx) => (
                                      <>
                                          <LotSourcesSubTable
                                              idx={idx}
                                              options={sourceLotOptions}
                                              onSearchChange={setSourceSearch}
                                              clientFilter={usingNote}
                                              labels={{
                                                  title: m.pll_new_sources_title(),
                                                  empty: m.pll_new_sources_empty(),
                                                  add: m.pll_new_source_add(),
                                                  remove: m.pll_new_source_remove(),
                                                  lotPlaceholder:
                                                      m.pll_new_source_lot_placeholder(),
                                                  qtyLabel:
                                                      m.pll_new_source_qty_label(),
                                              }}
                                          />
                                          <MaterialsSubTable
                                              idx={idx}
                                              options={materialLotOptions}
                                              onSearchChange={setMaterialSearch}
                                              clientFilter={usingPkgNote}
                                          />
                                      </>
                                  )
                                : undefined
                        }
                        headerAction={
                            <ScanLotCodeButton
                                parseLotId={makeLotIdParser(
                                    'sales-warehouse/lots',
                                )}
                                onScanned={(lotId) => {
                                    const current =
                                        form.getValues('items') ?? []
                                    form.setValue('items', [
                                        ...current,
                                        {
                                            kind: 'existing' as const,
                                            product_lot_id: lotId,
                                            quantity: 0,
                                        },
                                    ] as FormData['items'])
                                    void fetchAndCacheLot(lotId)
                                }}
                            />
                        }
                    />

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    {submitError && (
                        <div
                            role="alert"
                            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive whitespace-pre-line"
                        >
                            {submitError}
                        </div>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {isPending ? m.sl_new_submitting() : submitLabel}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}

// mergeLotOptions ghép lô seed (của item hiện có khi sửa) với lô active fetch
// được, dedupe theo value, ưu tiên nhãn fetch (kèm kho + tồn mới hơn). Lô pending
// không có trong fetch nên giữ nhãn seed → picker hiển thị đúng.
function mergeLotOptions(
    seed: ProductLotOption[],
    fetched: ProductLotOption[],
): ProductLotOption[] {
    const byValue = new Map<string, ProductLotOption>()
    for (const o of seed) byValue.set(o.value, o)
    for (const o of fetched) byValue.set(o.value, o)
    return Array.from(byValue.values())
}

// today trả local-date `YYYY-MM-DD` (không dùng toISOString — nó lệch sang UTC).
function today(): string {
    const d = new Date()
    const y = d.getFullYear()
    const mo = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${y}-${mo}-${dd}`
}

// addOneYear trả YYYY-MM-DD 1 năm sau `iso`. Ngày 29/2 → 28/2 năm sau.
function addOneYear(iso: string): string {
    if (!iso) return ''
    const [ys, ms, ds] = iso.split('-')
    const y = Number(ys)
    const mo = Number(ms)
    const d = Number(ds)
    if (!y || !mo || !d) return ''
    const dt = new Date(y + 1, mo - 1, d)
    if (dt.getMonth() !== mo - 1) dt.setDate(0)
    const ny = dt.getFullYear()
    const nm = String(dt.getMonth() + 1).padStart(2, '0')
    const nd = String(dt.getDate()).padStart(2, '0')
    return `${ny}-${nm}-${nd}`
}
